/**
 * 학생별 통계 페이지
 * 특정 학생의 전체 통계, 시도별 정답률, 답형식별 통계, 진도 추이 표시
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Space, Button, Select, Alert, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { StudentOverallCard } from '../../components/stats/StudentOverallCard';
import { AttemptAccuracyChart } from '../../components/stats/AttemptAccuracyChart';
import { AnswerTypeChart } from '../../components/stats/AnswerTypeChart';
import {
  getStudentOverallStats,
  getStudentAttemptStats,
  getAnswerTypeStats,
} from '../../services/statsService';
import {
  StudentOverallStats,
  StudentAttemptStatsResponse,
  AnswerTypeStatsResponse,
} from '../../types/stats';

export const StudentStatsPage: React.FC = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();

  const [overallStats, setOverallStats] = useState<StudentOverallStats | null>(null);
  const [attemptStats, setAttemptStats] = useState<StudentAttemptStatsResponse | null>(null);
  const [answerTypeStats, setAnswerTypeStats] = useState<AnswerTypeStatsResponse | null>(null);

  const [selectedWorkbook, setSelectedWorkbook] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 통계 데이터 로드
  const loadStats = async () => {
    if (!studentId) {
      setError('학생 ID가 없습니다.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 병렬로 3개 API 호출
      const [overall, attempts, answerTypes] = await Promise.all([
        getStudentOverallStats(studentId, selectedWorkbook),
        getStudentAttemptStats(studentId, selectedWorkbook),
        getAnswerTypeStats({ studentId, workbookName: selectedWorkbook }),
      ]);

      setOverallStats(overall);
      setAttemptStats(attempts);
      setAnswerTypeStats(answerTypes);
    } catch (err: any) {
      console.error('통계 로드 실패:', err);
      setError(err.response?.data?.detail || '통계를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, selectedWorkbook]);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          돌아가기
        </Button>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span>문제집 필터:</span>
          <Select
            style={{ width: 200 }}
            placeholder="전체 문제집"
            allowClear
            value={selectedWorkbook}
            onChange={setSelectedWorkbook}
          >
            {/* TODO: 문제집 목록 API 연동 */}
            <Select.Option value={undefined}>전체 문제집</Select.Option>
          </Select>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && <Alert message="에러" description={error} type="error" showIcon closable />}

      {/* 로딩 스피너 */}
      {loading && !overallStats ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* 전체 통계 카드 */}
          {overallStats && <StudentOverallCard stats={overallStats} loading={loading} />}

          {/* 시도별 정답률 차트 */}
          {attemptStats && (
            <AttemptAccuracyChart attempts={attemptStats.attempts} loading={loading} />
          )}

          {/* 답형식별 정답률 차트 */}
          {answerTypeStats && (
            <AnswerTypeChart answerTypes={answerTypeStats.answer_types} loading={loading} />
          )}
        </>
      )}
    </Space>
  );
};

export default StudentStatsPage;
